import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Skeleton,
    Tooltip,
} from "@mui/material";

const EllipsisTooltipText = ({ text, maxWidth = "240px" }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const el = textRef.current;
        if (!el) return;

        const checkTruncation = () => {
            setIsTruncated(el.scrollWidth > el.clientWidth);
        };

        checkTruncation();

        // Optional: re-check on window resize
        window.addEventListener("resize", checkTruncation);
        return () => window.removeEventListener("resize", checkTruncation);
    }, [text]);

    return (
        <Tooltip
            title={isTruncated ? text : ""}
            arrow
            disableHoverListener={!isTruncated}
            disableFocusListener={!isTruncated}
            disableTouchListener={!isTruncated}
        >
            <Typography
                ref={textRef}
                sx={{
                    color: "#061522",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "18px",
                    mb: "8px",
                    maxWidth: maxWidth,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: isTruncated ? "pointer" : "default",
                }}
            >
                {text}
            </Typography>
        </Tooltip>
    );
};

export const FeaturedArticleSection = ({
    onAddClick,
    selectedArticles = [],
    onRemoveArticle,
    loading,
}) => {
    return (
        <Box sx={{ mb: "24px" }}>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 500,
                    color: "#061522",
                    mb: "12px",
                    fontSize: 14,
                    fontFamily: "Inter",
                    lineHeight: "20px",
                }}
            >
                Featured Articles
            </Typography>

            <Typography
                sx={{
                    fontWeight: 400,
                    color: "#405261",
                    mb: "12px",
                    fontSize: 12,
                    fontFamily: "Inter",
                    lineHeight: "20px",
                }}
            >
                Highlight up to 6 articles on the top of your Help Center homepage
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "0px", mb: 2 }}>
                {loading ? (
                    Array.from({ length: 3 }).map((_, index) => (
                        <Box
                            key={index}
                            sx={{
                                py: "16px",
                                borderBottom: "1px solid #E5E9EC",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                            }}
                        >
                            <Box sx={{ width: "100%" }}>
                                <Skeleton variant="text" width="70%" height={24} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="40%" height={20} />
                            </Box>
                        </Box>
                    ))
                ) : (
                    selectedArticles.map((article) => (
                        <Box
                            key={article.id || article._id}
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                py: "16px",
                                borderBottom: "1px solid #E5E9EC",
                                "&:last-child": { borderBottom: "none" },
                            }}
                        >
                            <Box sx={{ display: "flex", gap: "8px" }}>
                                <Box sx={{ mt: "1px" }}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M8.00135 11.6709C7.81794 11.6709 7.66788 11.821 7.66988 12.0044C7.66988 12.1878 7.81994 12.3378 8.00335 12.3378C8.18676 12.3378 8.33683 12.1878 8.33683 12.0044C8.33683 11.821 8.18676 11.6709 8.00135 11.6709"
                                            stroke="#405261"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M8.00135 7.66895C7.81794 7.66895 7.66788 7.81901 7.66988 8.00242C7.66988 8.18583 7.81994 8.33589 8.00335 8.33589C8.18676 8.33589 8.33683 8.18583 8.33683 8.00242C8.33683 7.81901 8.18676 7.66895 8.00135 7.66895"
                                            stroke="#405261"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M8.00135 3.66797C7.81794 3.66797 7.66788 3.81803 7.66988 4.00144C7.66988 4.18485 7.81994 4.33491 8.00335 4.33491C8.18676 4.33491 8.33683 4.18485 8.33683 4.00144C8.33683 3.81803 8.18676 3.66797 8.00135 3.66797"
                                            stroke="#405261"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Box>

                                <Box>
                                    <EllipsisTooltipText text={article.title || "Untitled Article"} />

                                    <Typography
                                        sx={{
                                            color: "#405261",
                                            fontFamily: "Inter",
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            lineHeight: "18px",
                                        }}
                                    >
                                        {article.category || "No category"}
                                    </Typography>
                                </Box>
                            </Box>

                            <IconButton
                                onClick={() => onRemoveArticle(article)}
                                sx={{
                                    padding: "4px",
                                    "&:hover": { backgroundColor: "transparent" },
                                }}
                                disableRipple
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                >
                                    <path
                                        d="M5.33337 5.33301L10.6667 10.6663"
                                        stroke="#323232"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M10.6667 5.33301L5.33337 10.6663"
                                        stroke="#323232"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </IconButton>
                        </Box>
                    ))
                )}
            </Box>

            {!loading && (
                <>
                    <Button
                        onClick={onAddClick}
                        disabled={selectedArticles.length >= 6}
                        sx={{
                            width: 296,
                            height: 40,
                            borderRadius: "8px",
                            backgroundColor: "#148DFD",
                            color: "#FFF",
                            fontFamily: "Inter",
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: "18px",
                            textTransform: "none",
                            border: "1px solid #D5DCE1",
                            boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                            "&:hover": {
                                backgroundColor: "#148DFD",
                            },
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                            alignItems: "center",
                            px: "12px",
                            "&:disabled": {
                                backgroundColor: "#F9FAFA",
                                color: "#93A1B0",
                            },
                        }}
                    >
                        {selectedArticles.length >= 6 ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M11.5 6V19"
                                    stroke="#93A1B0"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M18 12.5H5"
                                    stroke="#93A1B0"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M11.5 6V19"
                                    stroke="#FFF"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M18 12.5H5"
                                    stroke="#FFF"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                        Add Articles
                    </Button>

                    <Box
                        sx={{
                            width: 296,
                            height: "1px",
                            backgroundColor: "#E5E9EC",
                            marginTop: "24px",
                        }}
                    />
                </>
            )}
        </Box>
    );
};